/**
 * MfaScreen — tela de verificação MFA para usuários Admin.
 *
 * Fase 0: stub local (qualquer código de 6 dígitos é aceito).
 * Fase 2: integrar TOTP real (Google Authenticator / Authy) via endpoint do IdP.
 *
 * Design: segue a identidade visual da LoginScreen (paleta Nava, estilos inline, pt-BR).
 * Acessibilidade: campo numérico, role="alert" no erro, focus automático, Enter confirma.
 *
 * Renderizada pelo AuthGuard quando status === 'mfa-required' (só para Admin).
 */
import { useState, useEffect, useRef } from 'react';
import { ShieldCheck, AlertCircle, ArrowRight } from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, NavaWordmark, NavaBackdrop } from '@/components/ui';
import { useAuth } from './AuthContext';

const INPUT_STYLE = {
  width: '100%',
  backgroundColor: C.black,
  border: `1px solid ${C.blackBorder}`,
  color: C.neutral,
  padding: '12px 14px',
  borderRadius: 6,
  fontSize: 24,
  letterSpacing: '0.3em',
  textAlign: 'center',
  outline: 'none',
  fontVariantNumeric: 'tabular-nums',
};

export function MfaScreen() {
  const { submitMfa, logout, error, isLoading } = useAuth();
  const [code, setCode] = useState('');
  const inputRef = useRef(null);

  // Foco automático no campo ao montar
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (e) => {
    // Permite apenas dígitos, máx 6
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(val);
  };

  const handleSubmit = () => {
    if (code.length === 6) submitMfa(code);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div
      style={{
        backgroundColor: C.black,
        minHeight: '100vh',
        color: C.neutral,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <NavaBackdrop />

      <div
        style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
        }}
      >
        <div style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
          <div className="mb-8 flex justify-center">
            <NavaWordmark size={48} />
          </div>

          {/* Ícone de shield */}
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: `${C.cyber}15`,
              border: `1px solid ${C.cyber}44`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}
            aria-hidden="true"
          >
            <ShieldCheck size={28} style={{ color: C.cyber }} />
          </div>

          <h1
            style={{
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              marginBottom: 8,
              color: C.neutral,
            }}
          >
            Verificação em dois fatores
          </h1>

          <p style={{ fontSize: 14, color: C.neutralDim, marginBottom: 28, lineHeight: 1.5 }}>
            Digite o código de 6 dígitos do seu aplicativo autenticador.
            <br />
            <span style={{ fontSize: 12, color: C.neutralMuted }}>
              Necessário para contas com perfil Administrador.
            </span>
          </p>

          <Card style={{ padding: 28, textAlign: 'left' }}>
            <label
              htmlFor="mfa-code"
              style={{ fontSize: 12, color: C.neutralDim, display: 'block', marginBottom: 8 }}
            >
              Código de autenticação
            </label>

            <input
              ref={inputRef}
              id="mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={code}
              onChange={handleChange}
              onKeyDown={handleKey}
              aria-describedby={error ? 'mfa-error' : undefined}
              style={{
                ...INPUT_STYLE,
                borderColor: error ? C.hot : C.blackBorder,
                marginBottom: 16,
              }}
            />

            {/* Indicador de progresso do código */}
            <div
              style={{
                display: 'flex',
                gap: 4,
                marginBottom: 16,
                justifyContent: 'center',
              }}
              aria-hidden="true"
            >
              {Array.from({ length: 6 }, (_, i) => (
                <div
                  key={i}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: i < code.length ? C.cyber : C.blackBorder,
                    transition: 'background-color 150ms',
                  }}
                />
              ))}
            </div>

            {error && (
              <div
                id="mfa-error"
                role="alert"
                style={{
                  marginBottom: 16,
                  padding: '10px 12px',
                  borderRadius: 6,
                  backgroundColor: `${C.hot}11`,
                  border: `1px solid ${C.hot}44`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <AlertCircle size={15} style={{ color: C.hot, flexShrink: 0 }} aria-hidden="true" />
                <span style={{ fontSize: 12, color: C.hotLight, lineHeight: 1.4 }}>{error}</span>
              </div>
            )}

            <Btn
              variant="primary"
              size="lg"
              icon={ArrowRight}
              onClick={handleSubmit}
              disabled={code.length !== 6 || isLoading}
            >
              {isLoading ? 'Verificando...' : 'Verificar'}
            </Btn>
          </Card>

          <button
            onClick={logout}
            style={{
              marginTop: 20,
              fontSize: 13,
              color: C.neutralMuted,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Voltar ao login
          </button>

          <div
            style={{
              marginTop: 20,
              fontSize: 12,
              color: C.neutralMuted,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <ShieldCheck size={12} aria-hidden="true" />
            MFA obrigatório · Perfil Administrador
          </div>
        </div>
      </div>
    </div>
  );
}
